/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import { PRODUCT_SEARCH_QUERY } from "../../api/queries";
import { productSearch } from "../../api/request";

const liveSearchProductsRequest = {
    /**
     * Fetch and process products from the catalog service
     *
     * @param {string} type - The type of product fetch ("next", "previous", "gender" or "initial")
     * @param {number} pagination - The page number to fetch
     * @returns {Promise<void>}
     */
    async getProducts(type = "next", pagination = 1) {
        try {
            // Set loading state and fetch products
            this.productsLoading = true;
            const result = await this.getCatalogProducts(type, pagination);

            // Remove skeleton loaders on first load
            if (!this.productsInitialLoaded) {
                this.removeSkeleton();
            }

            // Process the fetched products
            const products = this.mapProducts(result.productSearch.items, pagination);
            this.setPageInfo(this, result);

            // Determine how to handle the products based on load type
            const isInitialLoad = !this.productsInitialLoaded;
            const isFirstPage = pagination === 1 && type !== "previous";

            // Update products differently based on context
            if (isInitialLoad || isFirstPage) {
                // For initial load or first page: update facets, replace products and push to data layer
                this.sortNumericFacetBuckets(result.productSearch.facets, "bh_size");
                this.sortBucketsByCustomOrder(result.productSearch.facets, "m_size", this.facetConfig.customSortOrderAttributes.m_size);
                this.updateFacets(result.productSearch.facets);
                this.products = products;

                this.$nextTick(() => {
                    this.dataLayerPushItems(this.products);
                });
            } else {
                // For pagination: merge with existing products
                this.mergeProducts(products, type);
            }

            // Notify listeners that products have loaded
            this.dispatchProductListLoadedEvent(type, pagination);

            // Complete initial load setup if needed
            if (isInitialLoad) {
                this.productsInitialLoaded = true;
                this.handlePrevNextLinkTags();
            }
        } catch (error) {
            this.handleProductFetchError(error);
        } finally {
            this.productsLoading = false;
        }
    },
    /**
     * Fetch catalog products with the current search and filter settings
     *
     * @param {string} type
     * @param {number|null} pagination - The page number to fetch
     * @returns {Promise<Object>} Product search results or empty fallback object on error
     */
    async getCatalogProducts(type, pagination = null) {
        try {
            // Build search parameters with current filters and settings
            const searchParams = {
                phrase: this.searchPhrase,
                pageSize: this.pagesSize,
                currentPage: pagination,
                filter: this.createFilter(type),
                sort: this.getSortOptions()
            };

            // Execute the search query
            return await productSearch(this.apiUrl, PRODUCT_SEARCH_QUERY, searchParams, this.handleProductFetchError);
        } catch (error) {
            console.error("Error fetching catalog products:", error);

            // Return empty result structure to prevent UI errors
            // Full error handling is done in the calling method via handleProductFetchError()
            return {
                productSearch: {
                    facets: [],
                    items: []
                }
            };
        }
    }
}

export default liveSearchProductsRequest;
